import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Copy, TrendingUp, TrendingDown, Info, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const RegionalComparison = ({ 
  userOccupancy = Array(12).fill(0), 
  userAdr = Array(12).fill(0),
  onApplyRegionalData 
}) => {
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [regionalData, setRegionalData] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataFreshness, setDataFreshness] = useState(null);

  // Fetch available regions on mount
  useEffect(() => {
    fetchRegions();
  }, []);

  // Fetch comparison when region is selected or user data changes
  useEffect(() => {
    if (selectedRegion && regionalData) {
      compareData();
    }
  }, [selectedRegion, userOccupancy, userAdr]);

  const fetchRegions = async () => {
    try {
      const response = await axios.get(`${API}/regional-data/regions`);
      setRegions(response.data.regions);
      setDataFreshness(response.data.data_freshness);
    } catch (error) {
      console.error('Error fetching regions:', error);
      toast.error('Failed to load regional data');
    }
  };

  const fetchRegionalData = async (regionName) => {
    if (!regionName) return;
    
    setLoading(true);
    try {
      const encodedRegion = encodeURIComponent(regionName);
      const response = await axios.get(`${API}/regional-data/region/${encodedRegion}`);
      setRegionalData(response.data);
    } catch (error) {
      console.error('Error fetching regional data:', error);
      toast.error('Failed to load regional market data');
    } finally {
      setLoading(false);
    }
  };

  const compareData = async () => {
    if (!selectedRegion) return;

    try {
      const response = await axios.post(`${API}/regional-data/compare`, {
        region_name: selectedRegion,
        user_occupancy: userOccupancy,
        user_adr: userAdr
      });
      setComparison(response.data);
    } catch (error) {
      console.error('Error comparing data:', error);
    }
  };

  const handleRegionChange = (value) => {
    setSelectedRegion(value);
    fetchRegionalData(value);
  };

  const handleApplyRegionalData = () => {
    if (!regionalData) return;
    
    const occupancyData = regionalData.monthly_data.map(m => m.occupancy_rate);
    const adrData = regionalData.monthly_data.map(m => m.adr);
    
    onApplyRegionalData(occupancyData, adrData);
    toast.success(`Applied ${selectedRegion} market data to your budget`);
  };

  const getVarianceColor = (variance) => {
    if (variance > 5) return 'text-green-600 dark:text-green-400';
    if (variance < -5) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const getVarianceIcon = (variance) => {
    if (variance > 0) return <TrendingUp className="h-4 w-4 inline" />;
    return <TrendingDown className="h-4 w-4 inline" />;
  };

  return (
    <Card className="mt-6">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              Regional Market Comparison
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm">
                    <p>Compare your budget to the latest market data from {regions.length} Australian and New Zealand regions. 
                    Data sourced from Australian Accommodation Monitor (AAM), Tourism Research Australia (TRA), STR Benchmarking/CoStar, Accommodation Australia, and State Tourism Bodies. Updated daily.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
            <CardDescription>
              View the latest occupancy and ADR data for your region from AAM, TRA, STR/CoStar, Accommodation Australia & State Tourism Bodies
            </CardDescription>
          </div>
          {dataFreshness && (
            <Badge variant={dataFreshness.status === 'current' ? 'default' : 'destructive'}>
              Updated {dataFreshness.last_updated}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Region Selector */}
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="text-sm font-medium mb-2 block">Select Your Region</label>
            <Select value={selectedRegion} onValueChange={handleRegionChange}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a region to compare..." />
              </SelectTrigger>
              <SelectContent>
                {regions.map((region) => (
                  <SelectItem key={region.code} value={region.name}>
                    {region.name} ({region.market_segment})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {regionalData && (
            <Button 
              onClick={handleApplyRegionalData}
              className="gap-2"
            >
              <Copy className="h-4 w-4" />
              Apply Regional Data
            </Button>
          )}
        </div>

        {/* Regional Data Source Info */}
        {regionalData && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Data from {regionalData.data.source} • Last updated: {regionalData.data.last_updated}
              <br />
              Average Occupancy: <strong>{regionalData.averages.occupancy}%</strong> • Average ADR: <strong>${regionalData.averages.adr}</strong> <span className="text-muted-foreground text-xs">(excluding GST)</span>
            </AlertDescription>
          </Alert>
        )}

        {/* Comparison Table */}
        {loading && (
          <div className="text-center py-8">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground mt-2">Loading regional data...</p>
          </div>
        )}

        {regionalData && !loading && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24 whitespace-nowrap">Month</TableHead>
                  <TableHead className="text-center whitespace-nowrap">Regional Occ %</TableHead>
                  <TableHead className="text-center whitespace-nowrap">Your Occ %</TableHead>
                  <TableHead className="text-center whitespace-nowrap">Variance</TableHead>
                  <TableHead className="text-center whitespace-nowrap">Regional ADR</TableHead>
                  <TableHead className="text-center whitespace-nowrap">Your ADR</TableHead>
                  <TableHead className="text-center whitespace-nowrap">Variance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {MONTHS.map((month, index) => {
                  const regionalOcc = regionalData.monthly_data[index].occupancy_rate;
                  const regionalAdrVal = regionalData.monthly_data[index].adr;
                  const userOcc = userOccupancy[index] || 0;
                  const userAdrVal = userAdr[index] || 0;
                  const occVariance = (userOcc - regionalOcc).toFixed(1);
                  const adrVariance = (userAdrVal - regionalAdrVal).toFixed(0);

                  return (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{month}</TableCell>
                      <TableCell className="text-center">{regionalOcc}%</TableCell>
                      <TableCell className="text-center">{userOcc}%</TableCell>
                      <TableCell className={`text-center font-medium ${getVarianceColor(parseFloat(occVariance))}`}>
                        {occVariance > 0 ? '+' : ''}{occVariance}%
                        {occVariance !== '0.0' && getVarianceIcon(parseFloat(occVariance))}
                      </TableCell>
                      <TableCell className="text-center">${regionalAdrVal}</TableCell>
                      <TableCell className="text-center">${userAdrVal}</TableCell>
                      <TableCell className={`text-center font-medium ${getVarianceColor(parseFloat(adrVariance))}`}>
                        {adrVariance > 0 ? '+' : ''}${adrVariance}
                        {adrVariance !== '0' && getVarianceIcon(parseFloat(adrVariance))}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Summary Comparison */}
        {comparison && (
          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Occupancy Comparison</h4>
              <div className="text-2xl font-bold">
                {comparison.interpretation.occupancy_status === 'above' ? (
                  <span className="text-green-600 dark:text-green-400">
                    +{Math.abs(comparison.interpretation.occupancy_difference_pct).toFixed(1)}%
                  </span>
                ) : (
                  <span className="text-red-600 dark:text-red-400">
                    {comparison.interpretation.occupancy_difference_pct.toFixed(1)}%
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {comparison.interpretation.occupancy_status === 'above' ? 'Above' : 'Below'} regional average
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-sm">ADR Comparison</h4>
              <div className="text-2xl font-bold">
                {comparison.interpretation.adr_status === 'above' ? (
                  <span className="text-green-600 dark:text-green-400">
                    +{Math.abs(comparison.interpretation.adr_difference_pct).toFixed(1)}%
                  </span>
                ) : (
                  <span className="text-red-600 dark:text-red-400">
                    {comparison.interpretation.adr_difference_pct.toFixed(1)}%
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {comparison.interpretation.adr_status === 'above' ? 'Above' : 'Below'} regional average
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RegionalComparison;
